import React, { useState, useEffect, useCallback } from 'react';
import { Check, ChevronLeft, ChevronRight, AlertCircle, Star, Ban } from 'lucide-react';
import { kids } from '../utils/schedule';
import { checklistItemsAPI, completionsAPI, pollingAPI, redemptionsAPI, punishmentsAPI, ChecklistItem, Punishment } from '../services/api';
import { usePolling } from '../hooks/usePolling';
import RedeemModal from './RedeemModal';
import { Kid } from '../types/todo.types';
import { playSuccessSound } from '../utils/sound';
import { playCelebration } from '../utils/celebrationSound';

const ChecklistView: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [punishments, setPunishments] = useState<Punishment[]>([]);
  const [completedTasks, setCompletedTasks] = useState<{ [key: string]: boolean }>({});
  const [totalStars, setTotalStars] = useState<{ [kidName: string]: number }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starCounts, setStarCounts] = useState<{ [kidName: string]: number }>({});
  const [selectedKid, setSelectedKid] = useState<Kid | null>(null);
  const [jigglingKids, setJigglingKids] = useState<{ [kidName: string]: boolean }>({});
  const [dismissedCelebrations, setDismissedCelebrations] = useState<Set<string>>(new Set());

  // Helper to get local date key in YYYY-MM-DD format (avoiding UTC conversion issues)
  const getLocalDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Track what date the user was viewing (for detecting day change after sleep/background)
  const [lastViewedDateKey, setLastViewedDateKey] = useState<string>(() =>
    getLocalDateKey(new Date())
  );

  const dateKey = getLocalDateKey(currentDate);

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Update to today's date when page becomes visible after a day change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const todayDateKey = getLocalDateKey(new Date());
        // If the actual date has changed and user was viewing "today"
        if (todayDateKey !== lastViewedDateKey) {
          const wasViewingToday = dateKey === lastViewedDateKey;
          setLastViewedDateKey(todayDateKey);
          // If user was viewing "today", update to the new today
          if (wasViewingToday) {
            setCurrentDate(new Date());
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dateKey, lastViewedDateKey]);

  const fetchStarCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        kids.map(kid => redemptionsAPI.getStars(kid.name))
      );
      const counts: { [kidName: string]: number } = {};
      kids.forEach((kid, i) => { counts[kid.name] = results[i].stars; });
      setStarCounts(counts);
    } catch (err) {
      console.error('Error fetching star counts:', err);
    }
  }, []);

  // Fetch checklist items and completions when component mounts or date changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Convert JavaScript day (0=Sunday, 1=Monday, ..., 6=Saturday)
        // to ISO 8601 day number (1=Monday, 2=Tuesday, ..., 7=Sunday)
        const dayOfWeek = currentDate.getDay();
        const dayNumber = dayOfWeek === 0 ? 7 : dayOfWeek;

        // Fetch checklist items, completions, and total stars in parallel
        // Pass dateKey to include ad-hoc tasks for the specific date
        const [items, completions, stars] = await Promise.all([
          checklistItemsAPI.getByDayType(dayNumber, dateKey),
          completionsAPI.getByDate(dateKey),
          completionsAPI.getTotalStars()
        ]);

        setChecklistItems(items);
        setCompletedTasks(completions);
        setTotalStars(stars);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    // Punishments are fetched separately so a failure only hides the section
    // instead of taking down the whole checklist.
    const fetchPunishments = async () => {
      try {
        setPunishments(await punishmentsAPI.getForDate(dateKey));
      } catch (err) {
        console.error('Error fetching punishments:', err);
        setPunishments([]);
      }
    };

    fetchData();
    fetchPunishments();
    fetchStarCounts();
  }, [currentDate, dateKey, fetchStarCounts]);

  // Poll for completion updates (only when viewing today)
  const pollCompletions = useCallback(async () => {
    // Only poll when viewing today and not in loading state
    if (!isToday(currentDate) || loading) return;

    try {
      const updatedCompletions = await pollingAPI.pollCompletions(dateKey);
      // Only update if data changed (not 304)
      if (updatedCompletions !== null) {
        setCompletedTasks(updatedCompletions);
      }
    } catch (err) {
      // Silently ignore polling errors to avoid disrupting the UI
      console.error('[Polling] Error fetching completions:', err);
    }
  }, [currentDate, dateKey, loading]);

  // Enable polling for today's view
  usePolling(pollCompletions, {
    interval: 30000, // Poll every 30 seconds
    enabled: isToday(currentDate) && !loading,
    pollImmediately: false // Don't poll immediately, we already fetch on mount
  });

  const triggerCompletionFeedback = (kidName: string, allDone: boolean) => {
    // Big ~10s celebration jingle when the last item is checked, otherwise the short chime
    if (allDone) {
      playCelebration();
    } else {
      playSuccessSound();
    }
    setJigglingKids(prev => ({ ...prev, [kidName]: true }));
    window.setTimeout(() => {
      setJigglingKids(prev => ({ ...prev, [kidName]: false }));
    }, 600);
  };

  const toggleComplete = async (kidName: string, checklistItemId: string) => {
    const taskKey = `${dateKey}-${kidName}-${checklistItemId}`;
    const wasCompleted = completedTasks[taskKey] || false;

    // Celebrate only when transitioning to completed.
    // (Audio must be triggered here, inside the tap handler, so iOS unlocks it.)
    if (!wasCompleted) {
      const othersDone = getItemsForKid(kidName)
        .filter(item => item._id !== checklistItemId)
        .every(item => isCompleted(kidName, item._id));
      triggerCompletionFeedback(kidName, othersDone);
    }

    // Optimistic update for daily tasks
    setCompletedTasks(prev => ({
      ...prev,
      [taskKey]: !prev[taskKey]
    }));

    // Optimistic update for total stars
    setTotalStars(prev => ({
      ...prev,
      [kidName]: (prev[kidName] || 0) + (wasCompleted ? -1 : 1)
    }));

    try {
      // Update via API
      await completionsAPI.toggle(dateKey, kidName, checklistItemId);
      // Invalidate ETag cache so next poll fetches fresh data
      pollingAPI.invalidateCompletions(dateKey);
      fetchStarCounts();
    } catch (err) {
      console.error('Error toggling completion:', err);
      // Revert optimistic updates on error
      setCompletedTasks(prev => ({
        ...prev,
        [taskKey]: !prev[taskKey]
      }));
      setTotalStars(prev => ({
        ...prev,
        [kidName]: (prev[kidName] || 0) + (wasCompleted ? 1 : -1)
      }));
    }
  };

  const isCompleted = (kidName: string, checklistItemId: string) => {
    const taskKey = `${dateKey}-${kidName}-${checklistItemId}`;
    return completedTasks[taskKey] || false;
  };

  const getItemsForKid = (kidName: string): ChecklistItem[] => {
    return checklistItems.filter(item => item.kids.includes(kidName));
  };

  const getPunishmentsForKid = (kidName: string): Punishment[] => {
    return punishments.filter(p => p.kidName === kidName);
  };

  // "2026-10-31" -> "31 Oct" (parsed as local date to avoid UTC shifting the day)
  const formatUntil = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${d} ${month}`;
  };

  const getStarCount = (kidName: string): number => {
    // Count completed tasks for this kid based on their checklist items
    const items = getItemsForKid(kidName);
    return items.filter(item => isCompleted(kidName, item._id)).length;
  };

  const isAllDone = (kidName: string, items: ChecklistItem[]): boolean => {
    return items.length > 0 && items.every(item => isCompleted(kidName, item._id));
  };

  const celebrationKey = (kidName: string) => `${dateKey}-${kidName}`;

  const dismissCelebration = (kidName: string): void => {
    setDismissedCelebrations(prev => {
      const next = new Set(prev);
      next.add(celebrationKey(kidName));
      return next;
    });
  };

  // Once a column is no longer fully complete, allow the celebration to show again next time
  useEffect(() => {
    setDismissedCelebrations(prev => {
      let changed = false;
      const next = new Set(prev);
      kids.forEach(kid => {
        const key = celebrationKey(kid.name);
        if (next.has(key) && !isAllDone(kid.name, getItemsForKid(kid.name))) {
          next.delete(key);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedTasks, checklistItems, dateKey]);

  const goToPreviousDay = (): void => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const goToNextDay = (): void => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = (): void => {
    setCurrentDate(new Date());
  };

  const formatDate = (date: Date): string => {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${weekday}, ${day} ${month}, ${year}`;
  };

  const isTodayView = isToday(currentDate);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-500 mx-auto mb-6"></div>
          <p className="text-2xl md:text-3xl font-semibold text-gray-700">Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-xl p-8 md:p-10 max-w-2xl">
          <div className="flex items-center gap-4 mb-6 text-red-600">
            <AlertCircle size={48} />
            <h2 className="text-3xl md:text-4xl font-bold">Error Loading Checklist</h2>
          </div>
          <p className="text-xl md:text-2xl text-gray-700 mb-6">{error}</p>
          <p className="text-lg text-gray-600 mb-6">
            Make sure the backend server is running: <code className="bg-gray-100 px-3 py-2 rounded text-base">npm run server</code>
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-8 py-5 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white text-xl md:text-2xl font-semibold rounded-xl transition touch-manipulation"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Date Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-3 mb-4">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={goToPreviousDay}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-lg transition flex items-center gap-1 text-sm font-semibold touch-manipulation"
            >
              <ChevronLeft size={20} /> <span className="hidden md:inline">Prev</span>
            </button>

            <div className="text-center flex-1">
              <div className="text-base md:text-lg font-bold text-gray-800 mb-1">
                {formatDate(currentDate)}
              </div>
              <div className="flex gap-2 items-center justify-center flex-wrap">
                {!isToday(currentDate) && (
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 bg-blue-100 hover:bg-blue-200 active:bg-blue-300 text-blue-700 rounded-lg text-xs font-medium transition touch-manipulation"
                  >
                    Today
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={goToNextDay}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-lg transition flex items-center gap-1 text-sm font-semibold touch-manipulation"
            >
              <span className="hidden md:inline">Next</span> <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Total Stars Display */}
        <div className="bg-gradient-to-r from-yellow-100 to-amber-100 rounded-xl shadow-lg p-3 mb-4">
          <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
            {kids.map(kid => (
              <div key={kid.name} className="flex items-center gap-2 px-3 py-1.5 bg-white/60 rounded-lg">
                <span className="text-xl md:text-2xl">{kid.avatar}</span>
                <span className={`${kid.textColor} font-bold text-sm md:text-base`}>{kid.name}</span>
                <div className="flex items-center gap-0.5">
                  <Star size={16} className="text-yellow-500 fill-yellow-400" />
                  <span className="text-yellow-700 font-bold text-base md:text-lg">{starCounts[kid.name] ?? 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kids.map(kid => {
            const items = getItemsForKid(kid.name);
            const kidPunishments = getPunishmentsForKid(kid.name);
            const starCount = getStarCount(kid.name);
            const showCelebration = isAllDone(kid.name, items) && !dismissedCelebrations.has(celebrationKey(kid.name));
            return (
              <div key={kid.name} className={`${kid.color} p-4 md:p-5 rounded-xl shadow-xl relative overflow-hidden`}>
                {showCelebration && (
                  <button
                    onClick={() => dismissCelebration(kid.name)}
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-yellow-200/90 backdrop-blur-sm rounded-xl cursor-pointer touch-manipulation animate-fade-in"
                    aria-label={`${kid.name} finished everything! Tap to dismiss`}
                    data-testid={`celebration-${kid.name}`}
                  >
                    <Star
                      className="text-yellow-400 fill-yellow-400 drop-shadow-lg animate-bounce"
                      style={{ width: '60%', height: 'auto', maxWidth: 220 }}
                      strokeWidth={1.5}
                    />
                    <span className="text-2xl md:text-3xl font-extrabold text-gray-800 text-center px-4">
                      {kid.name} is all done! 🎉
                    </span>
                    <span className="text-sm text-gray-600">Tap to dismiss</span>
                  </button>
                )}
                <button
                  onClick={() => setSelectedKid(kid)}
                  className="w-full flex items-center gap-2 mb-3 pb-2 border-b-2 border-gray-300 cursor-pointer touch-manipulation hover:opacity-80 transition text-left"
                >
                  <span className={`text-3xl md:text-4xl ${jigglingKids[kid.name] ? 'animate-jiggle' : ''}`}>{kid.avatar}</span>
                  <h2 className={`${kid.textColor} font-bold text-xl md:text-2xl flex-1`}>
                    <span className={jigglingKids[kid.name] ? 'animate-jiggle' : ''}>{kid.name}</span>
                  </h2>
                  <div className="flex items-center gap-1 bg-yellow-100 px-2 py-1 rounded-lg">
                    <Star size={18} className="text-yellow-500 fill-yellow-400" />
                    <span className="text-yellow-700 font-bold text-lg">{starCounts[kid.name] ?? 0}</span>
                  </div>
                </button>
                {kidPunishments.length > 0 && (
                  <div
                    className="mb-3 p-2.5 bg-red-100 border-2 border-red-300 rounded-lg"
                    data-testid={`punishment-${kid.name}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5 text-red-700">
                      <Ban size={16} className="stroke-[2.5]" />
                      <span className="font-bold text-xs md:text-sm uppercase tracking-wide">Punishment</span>
                    </div>
                    <ul className="space-y-1">
                      {kidPunishments.map(p => (
                        <li key={p._id} className="flex items-start gap-2 text-sm md:text-base">
                          <span className="text-lg leading-none">{p.emoji || '🚫'}</span>
                          <div className="flex-1">
                            <span className="font-semibold text-red-900">{p.description}</span>
                            <span className="block text-xs text-red-700">Until {formatUntil(p.endDate)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <ul className="space-y-1.5">
                  {items.map((item) => {
                    const completed = isCompleted(kid.name, item._id);
                    return (
                      <li key={item._id}>
                        <button
                          onClick={() => toggleComplete(kid.name, item._id)}
                          disabled={!isTodayView}
                          className={`w-full flex items-center gap-2.5 p-2 rounded-lg transition-all touch-manipulation ${
                            !isTodayView
                              ? 'opacity-60 cursor-not-allowed bg-gray-100'
                              : completed
                                ? 'bg-green-100 hover:bg-green-200 active:bg-green-300'
                                : 'bg-white hover:bg-gray-50 active:bg-gray-100'
                          }`}
                          aria-label={completed ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          <div className={`flex-shrink-0 w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded border-2 ${
                            !isTodayView
                              ? 'bg-gray-200 border-gray-300'
                              : completed
                                ? 'bg-green-500 border-green-600'
                                : 'bg-white border-gray-300'
                          }`}>
                            {completed && <Check size={16} className={isTodayView ? 'text-white stroke-[3]' : 'text-gray-400 stroke-[3]'} />}
                          </div>
                          <span
                            className={`text-sm md:text-base font-medium text-left flex-1 ${
                              !isTodayView
                                ? 'text-gray-400'
                                : completed
                                  ? 'line-through text-gray-500'
                                  : 'text-gray-800'
                            }`}
                          >
                            {item.task}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {selectedKid && (
          <RedeemModal
            kid={selectedKid}
            onClose={() => setSelectedKid(null)}
            onRedeem={fetchStarCounts}
          />
        )}
      </div>
    </div>
  );
};

export default ChecklistView;
